// vg-binding: P7.D-08
//
// apps/vendor/src/features/products/ProductsTabs.tsx
//
// 5 tabs per Task 27 spec: All / Active (live + inactive_countdown) /
// To Complete (pending) / Inactive / Rejected.

import { cn } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import type { ProductState } from './api.js';

export type ProductsTabKey = 'all' | 'active' | 'to_complete' | 'inactive' | 'rejected';

interface Tab {
  key: ProductsTabKey;
  labelKey: string;
  testId: string;
}

const TABS: readonly Tab[] = [
  { key: 'all', labelKey: 'tabs.all', testId: 'products-tab-all' },
  { key: 'active', labelKey: 'tabs.active', testId: 'products-tab-active' },
  { key: 'to_complete', labelKey: 'tabs.toComplete', testId: 'products-tab-to-complete' },
  { key: 'inactive', labelKey: 'tabs.inactive', testId: 'products-tab-inactive' },
  { key: 'rejected', labelKey: 'tabs.rejected', testId: 'products-tab-rejected' },
];

interface Props {
  active: ProductsTabKey;
  onChange: (tab: ProductsTabKey) => void;
}

export function ProductsTabs({ active, onChange }: Props): JSX.Element {
  const { t } = useTranslation('products');
  return (
    <nav
      aria-label={t('tabs.ariaLabel')}
      className="flex w-full items-center gap-1 overflow-x-auto border-b border-pw-border"
    >
      {TABS.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            data-testid={tab.testId}
            onClick={() => onChange(tab.key)}
            className={cn(
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'border-pw-primary text-pw-primary-strong'
                : 'border-transparent text-pw-text-muted hover:text-pw-text',
            )}
          >
            {t(tab.labelKey)}
          </button>
        );
      })}
    </nav>
  );
}

/** Returns the BE state-filter value for the given tab. `null` means "no filter". */
export function tabKeyToBeFilter(
  key: ProductsTabKey,
): ProductState | 'active-compound' | null {
  switch (key) {
    case 'all':
      return null;
    case 'active':
      return 'active-compound'; // FE-side filter; BE doesn't union states
    case 'to_complete':
      return 'pending';
    case 'inactive':
      return 'inactive';
    case 'rejected':
      return 'rejected';
  }
}

export default ProductsTabs;
